import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Panel, SectionTitle } from '../../../../components';
import { footerWordCount, isFooterHtmlEmpty } from '../../footer-html';
import { firstErrorMessage, firstWarningMessage, issuesFor } from '../../issues-by-field';
import { resetSiteText, resolveFooterText, setRouteText, setSiteText } from '../../seo-document';
import type { SiteSeoDocument, SiteSeoFooterRule, SiteSeoIssue, SiteSeoRoute } from '../../seo-types';
import { PLACEHOLDER_RE } from '../../seo-validation';
import { footerRulePathOf } from '../../route-path';
import { FieldTag } from '../FieldTag';
import { FooterTextEditor } from '../FooterTextEditor';
import { Body, ErrorText, Head, HintText, Note, SourceTag, TagRow } from './FooterPanel.styles';

export type FooterPanelProps = {
  draft: SiteSeoDocument;
  /** The footer rules the site shows now, in the order its source produces them (the platform's among them). */
  liveFooterRules: SiteSeoFooterRule[];
  /** null = Site defaults (the `*` rule). */
  route: SiteSeoRoute | null;
  language: string;
  defaultLanguage: string;
  /** False when the site's footer source cannot take the owner's text: everything is read-only. */
  overridable: boolean;
  /** False when publish copies the platform's footer texts instead of referencing them (Site defaults says so). */
  reference: boolean;
  /** The server's issues; the text shows its first error and its html-stripped warning as is. */
  issues: SiteSeoIssue[];
  onChange: (update: (draft: SiteSeoDocument) => SiteSeoDocument) => void;
  generateSlot?: React.ReactNode;
};

type Mode = 'edit' | 'view' | 'locked';

/**
 * Footer SEO text, for a route or for the site (`*`). It shows what the site will show, through the site's own rule
 * matcher (`resolveFooterText`): a route without its own text sees the rule it inherits, read-only and tagged with
 * that rule's path, until the owner chooses "Write a text for this page". A pattern route's text is published as its
 * derived wildcard, which the panel names. Read-only when the site's footer takes no override, or when the route
 * derives no footer rule of its own (`/:lang/x`). An empty text is never stored: clearing the editor drops the value.
 */
export const FooterPanel = ({
  draft,
  liveFooterRules,
  route,
  language,
  defaultLanguage,
  overridable,
  reference,
  issues,
  onChange,
  generateSlot,
}: FooterPanelProps): React.ReactElement => {
  const { t } = useTranslation();
  // The language the owner chose to write in: the editor stays open while it is empty (an empty value cannot be
  // stored, so it is this panel's state). Keyed by language, so another language starts from what it shows.
  const [writingFor, setWritingFor] = React.useState<string | null>(null);

  const path = route ? route.path : null;
  const ownMap = route ? route.footer_text : draft.site.footer_text;
  const ownText = ownMap?.[language] ?? '';
  const resolved = resolveFooterText(draft, liveFooterRules, route, language, defaultLanguage);
  const rulePath = route ? footerRulePathOf(route) : '*';
  const locked = !overridable || rulePath === null;
  const writing = writingFor === language;
  const mode: Mode = locked
    ? 'locked'
    : ownText !== '' || writing || isFooterHtmlEmpty(resolved.value)
      ? 'edit'
      : 'view';

  const write = (html: string): void => {
    const value = isFooterHtmlEmpty(html) ? '' : html;
    setWritingFor(language);
    onChange((d) =>
      path === null
        ? setSiteText(d, 'footer_text', language, value)
        : setRouteText(d, path, 'footer_text', language, value),
    );
  };
  const reset = (): void => {
    setWritingFor(null);
    onChange((d) => resetSiteText(d, 'footer_text', language, defaultLanguage));
  };

  const fieldIssues = issuesFor(issues, path, 'footer_text', language);
  const error = firstErrorMessage(fieldIssues);
  const stripped = fieldIssues.find((i) => i.level === 'warning' && i.code === 'html-stripped' && i.message !== '');
  // The html-stripped note shows on its own line above (even beside an error); any other warning only while no error.
  const warning = error ? undefined : firstWarningMessage(fieldIssues.filter((i) => i.code !== 'html-stripped'));

  /** Where the text on show comes from, while the owner has none of their own in this language. */
  const sourceTag = (): React.ReactNode => {
    const usingDefault = ownText === '' && (route === null ? resolved.source === 'site' : resolved.source === 'own');
    if (usingDefault) return <FieldTag kind="using-default-language" language={defaultLanguage} />;
    if (route === null) return resolved.source === 'platform' ? <FieldTag kind="platform-default" /> : null;
    switch (resolved.source) {
      case 'platform':
        return <SourceTag text="Now showing the platform's text" tag="span" />;
      case 'site':
        return (
          <SourceTag text="Now showing the text from {{path}}" placeholders={{ path: t('Site defaults') }} tag="span" />
        );
      case 'wildcard':
        return (
          <SourceTag
            text="Now showing the text from {{path}}"
            placeholders={{ path: resolved.fromPath ?? '' }}
            tag="span"
          />
        );
      default:
        return null;
    }
  };

  const editorKey = `${path ?? '*'}|${language}|${mode}`;
  const label = t('Footer SEO text');

  return (
    <Panel>
      <Head>
        <SectionTitle title="Footer SEO text" />
        {!locked ? generateSlot : null}
      </Head>
      <Body data-field="footer_text" data-tone={stripped || warning ? 'warning' : undefined}>
        {route === null && !reference ? (
          <Note text="Platform footer texts reach your site at your next publish." tag="paragraph" />
        ) : null}
        {mode === 'locked' ? (
          <>
            <Note
              text={
                !overridable
                  ? "Footer text can't be edited for this site yet."
                  : "This page's footer text is set on Site defaults."
              }
              tag="paragraph"
            />
            {!isFooterHtmlEmpty(resolved.value) ? (
              <FooterTextEditor
                key={editorKey}
                label={label}
                html={resolved.value}
                onChange={() => undefined}
                readOnly
              />
            ) : null}
          </>
        ) : null}
        {mode === 'view' ? (
          <>
            <TagRow>{sourceTag()}</TagRow>
            <FooterTextEditor key={editorKey} label={label} html={resolved.value} onChange={() => undefined} readOnly />
            <TagRow>
              <Button
                label={route === null ? t('Write your own text') : t('Write a text for this page')}
                variant="outline"
                onClick={() => setWritingFor(language)}
              />
            </TagRow>
          </>
        ) : null}
        {mode === 'edit' ? (
          <>
            {route?.match === 'pattern' && rulePath !== null ? (
              <Note text="Applies to every page under {{wildcard}}" placeholders={{ wildcard: rulePath }} tag="span" />
            ) : null}
            <FooterTextEditor key={editorKey} label={label} html={ownText} onChange={write} focusOnMount={writing} />
            <TagRow>
              {route === null ? (
                <>
                  <FieldTag kind={draft.site.footer_text === undefined ? 'platform-default' : 'edited'} />
                  {/* On another language than the default, only once it has its own text (Reset drops just that). */}
                  {draft.site.footer_text !== undefined && (language === defaultLanguage || ownText !== '') ? (
                    <Button label={t('Reset to platform default')} variant="text" onClick={reset} />
                  ) : null}
                </>
              ) : null}
              <Note text="Words: {{count}}" placeholders={{ count: footerWordCount(ownText) }} tag="span" />
            </TagRow>
            {PLACEHOLDER_RE.test(ownText) ? (
              <HintText text="Replace the [bracketed] placeholders with your own facts" tag="span" />
            ) : null}
          </>
        ) : null}
        {stripped ? <HintText text={stripped.message} raw tag="span" /> : null}
        {error ? <ErrorText text={error} raw tag="span" /> : null}
        {warning ? <HintText text={warning} raw tag="span" /> : null}
      </Body>
    </Panel>
  );
};

export default FooterPanel;
